/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import {translate} from '@ion/core/src/locale/locale';
import {Device} from '@ion/core/src/types/entities';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {dlsColor} from '@ion/dls/src/theme/theme';

interface GetAssociationSummaryArgs {
  currentDevice?: Device;
  associatedDevice?: Device;
  associatedIdentifier?: string;
  isCurrentPrimary: boolean;
  currentStatus?: string;
  associatedStatus?: string;
}

interface GetWorkInProgressAlertPropsArgs {
  associationState: string;
}

export interface WorkInProgressAlertProps {
  type: 'neutral';
  iconColor: string;
  title: string;
  message: string;
}

const DDSAR_PRIMARY_TAG = 'ddsar-primary';
const DDSAR_SECONDARY_TAG = 'ddsar-secondary';
const LMU_CATEGORY = 'LMU';
const ATU_CATEGORY = 'ATU';

const STATE_ENTRIES: Record<string, string> = {
  pendingDisable          : 'deactivationInProgress',
  pendingEnableCurrent    : 'activationPending',
  pendingEnableAssociated : 'enabledPendingPairing'
};

const formatIdReportTime = (timestamp?: string) => {
  return timestamp ? formatDateTime(timestamp) : '--';
};

/**
 * Resolves whether the current device is the DDSAR primary device.
 * Prefer explicit DDSAR association tags when present; fall back to category.
 */
export const resolveIsCurrentPrimary = (device?: Device): boolean => {
  const associatedDevices = device?.extensions?.associatedDevices;

  if (Array.isArray(associatedDevices)) {
    const hasPrimaryTag = associatedDevices.some(({tags}) => tags?.includes?.(DDSAR_PRIMARY_TAG));
    const hasSecondaryTag = associatedDevices.some(({tags}) => tags?.includes?.(DDSAR_SECONDARY_TAG));
    if (hasSecondaryTag) {
      return true;
    }

    if (hasPrimaryTag) {
      return false;
    }
  }

  if (device?.category === LMU_CATEGORY) {
    return true;
  }

  if (device?.category === ATU_CATEGORY) {
    return false;
  }

  return false;
};

/**
 * Builds normalized Primary/Secondary summary data used by the pending alert content.
 */
export const getAssociationSummary = ({
  currentDevice,
  associatedDevice,
  associatedIdentifier,
  isCurrentPrimary,
  currentStatus,
  associatedStatus
}: GetAssociationSummaryArgs) => {
  const primaryDevice   = isCurrentPrimary ? currentDevice : associatedDevice;
  const secondaryDevice = isCurrentPrimary ? associatedDevice : currentDevice;

  const getESN = ({esn}: {esn?: string} = {}, useAssociatedIdentifier?: boolean): string => (
    esn || (useAssociatedIdentifier ? associatedIdentifier : (currentDevice?.esn || '--')) || '--'
  );

  const primaryESN   = getESN(primaryDevice, !isCurrentPrimary);
  const secondaryESN = getESN(secondaryDevice, isCurrentPrimary);

  return {
    primaryESN,
    secondaryESN,
    primaryLastIdReport   : formatIdReportTime(primaryDevice?.extensions?.idReportTimestamp),
    secondaryLastIdReport : formatIdReportTime(secondaryDevice?.extensions?.idReportTimestamp),
    primaryStatus         : isCurrentPrimary ? currentStatus : associatedStatus,
    secondaryStatus       : isCurrentPrimary ? associatedStatus : currentStatus
  };
};

/**
 * Maps the association state to alert type, icon color, and localized copy.
 */
export const getWorkInProgressAlertProps = ({
  associationState
}: GetWorkInProgressAlertPropsArgs): WorkInProgressAlertProps => {
  const entrySuffix = STATE_ENTRIES[associationState ?? ''];

  if (!entrySuffix) {
    return {
      type      : 'neutral',
      iconColor : dlsColor('grey3'),
      title     : '',
      message   : ''
    };
  }

  return {
    iconColor : dlsColor('grey3'),
    title     : translate(`admin-devices:view.sentinel.workInProgress.${entrySuffix}.title`),
    message   : translate(`admin-devices:view.sentinel.workInProgress.${entrySuffix}.message`),
    type      : 'neutral'
  };
};
